import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  CommunicationType,
  CommunicationDirection,
  CreateCommunicationLogDto,
  getTypeDisplayName,
  getDirectionDisplayName
} from '../../services/communicationLogService';

interface CommunicationLogFormProps {
  jobId: string;
  customerId?: string;
  onSubmit: (data: CreateCommunicationLogDto) => Promise<void>;
  isSubmitting: boolean;
}

const CommunicationLogForm: React.FC<CommunicationLogFormProps> = ({
  jobId,
  customerId,
  onSubmit,
  isSubmitting
}) => {
  const [type, setType] = useState<CommunicationType>(CommunicationType.Phone);
  const [direction, setDirection] = useState<CommunicationDirection>(CommunicationDirection.Outgoing);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: CreateCommunicationLogDto = {
      jobId,
      customerId,
      type,
      direction,
      subject: subject.trim(),
      content: content.trim(),
      communicationDate: new Date().toISOString()
    };

    await onSubmit(data);

    // Reset form on success
    setSubject('');
    setContent('');
  };

  const typeOptions = [
    CommunicationType.Email,
    CommunicationType.Phone,
    CommunicationType.SMS,
    CommunicationType.InPerson,
    CommunicationType.VideoCall,
    CommunicationType.Other
  ];

  const directionOptions = [
    CommunicationDirection.Incoming,
    CommunicationDirection.Outgoing
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Add Communication Log
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as CommunicationType)}
                label="Type"
              >
                {typeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {getTypeDisplayName(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                value={direction}
                onChange={(e) => setDirection(e.target.value as CommunicationDirection)}
                label="Direction"
              >
                {directionOptions.map((d) => (
                  <MenuItem key={d} value={d}>
                    {getDirectionDisplayName(d)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the communication"
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Details of the communication..."
              multiline
              rows={3}
              inputProps={{ maxLength: 4000 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                disabled={isSubmitting || (!subject.trim() && !content.trim())}
              >
                {isSubmitting ? 'Adding...' : 'Add Log'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default CommunicationLogForm;
